/**
 * Messaging — direct messaging page (production rebuild).
 *
 * Mirrors the prototype's WhatsApp / Telegram / LinkedIn-DM aesthetic but
 * is wired end-to-end to the existing messagingService + MessagingContext.
 * No backend changes.
 *
 * Real-time substitute (polling)
 *   • Conversation list refreshes every 10 s when the page is visible
 *   • Active chat messages refresh every 5 s when a chat is open + visible
 *   • Both pollers pause on tab-hide and resume on tab-focus
 *
 * Lazy loading
 *   • Conv list — initial 30, fetch next page when scroll < 200 px from bottom
 *   • Messages — initial 50 newest, scroll-to-top loads older with scroll
 *     position preserved (no view jump)
 *
 * Auto-scroll (the "don't yank the user" rule)
 *   • New message arrives → auto-scroll only if user was already near the
 *     bottom (within 100 px). Otherwise show a "↓ N new messages" pill.
 *
 * Anti-regression — every production behaviour preserved:
 *   • ?profileId=NNN deep-link → auto-open or create conversation
 *   • markAsRead on conv-select → optimistic unread decrement on context
 *   • registerConversationListCallback for external silent refresh hooks
 *   • Two-pane mobile responsive (list ↔ chat with back button)
 *   • ConfirmDialog on delete
 *   • New-conversation Dialog backed by ConnectionService.getConnections
 *
 * Dropped vs prototype
 *   • Fake auto-reply 2 s after user sends — misleading in production
 *   • Real-time typing indicator from other person — no WebSocket yet;
 *     animation primitive kept for future use
 */

import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Skeleton } from 'primereact/skeleton';

import messagingService from '../../services/messagingService';
import ConnectionService from '../../services/agentProfile/ConnectionService';
import { useMessaging } from '../../contexts/MessagingContext';

// ─── Helpers ────────────────────────────────────────────────────────────
const COLOR_PALETTE = ['blue', 'indigo', 'purple', 'pink', 'teal', 'green', 'orange', 'cyan'];
const colorFor = (id) => {
    if (!id) return COLOR_PALETTE[0];
    const n = typeof id === 'number' ? id : String(id).split('').reduce((s, c) => s + c.charCodeAt(0), 0);
    return COLOR_PALETTE[Math.abs(n) % COLOR_PALETTE.length];
};

const initialsFrom = (name) => {
    if (!name) return '??';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || '??';
};

const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diff)) return '';
    const m = Math.floor(diff / 60000);
    if (m < 1)  return 'just now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7)  return `${d}d`;
    return `${Math.floor(d / 7)}w`;
};

// Online / presence indicator is intentionally NOT rendered. The backend
// has no `last_active_at` or WebSocket presence channel yet, so showing a
// "green dot" or "online" status would be misleading. We'll wire this in
// at the WebSocket stage. The Avatar wrapper structure is preserved so
// re-adding the dot will be a one-line change.

// Resolve a profile photo URL across the different field names the
// backend returns: messaging API returns `profile_photo_url`,
// agent-profile API returns `profile_photo_url_full`. Fall through both.
const photoUrlFor = (p) => p?.profile_photo_url_full || p?.profile_photo_url || p?.photo_url || null;

// Day label for separators between message groups.
const dayLabel = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    const sameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
    if (sameDay(d, today)) return 'Today';
    if (sameDay(d, yesterday)) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const POLL_LIST_MS = 15_000;
const POLL_CHAT_MS = 8_000;
const CONV_PAGE_SIZE = 30;
const MSG_PAGE_SIZE = 50;

// ═════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════
const Messaging = () => {
    const navigate = useNavigate();
    const { convId: routeConvId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useRef(null);

    const { setUnreadCount, fetchUnreadCount, registerConversationListCallback } = useMessaging();

    // ── State ────────────────────────────────────────────────────────────
    const [conversations, setConversations] = useState([]);
    const [convsLoading, setConvsLoading] = useState(true);
    const [convsHasMore, setConvsHasMore] = useState(false);
    const [convsPage, setConvsPage] = useState(1);
    const [convsLoadingMore, setConvsLoadingMore] = useState(false);

    const [activeConvId, setActiveConvId] = useState(null);
    const [messagesByConv, setMessagesByConv] = useState({});
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [olderHasMoreByConv, setOlderHasMoreByConv] = useState({});
    const [olderLoading, setOlderLoading] = useState(false);
    const [newMessagesPill, setNewMessagesPill] = useState(0);

    const [search, setSearch] = useState('');
    const [composerValue, setComposerValue] = useState('');
    const [pendingFile, setPendingFile] = useState(null);
    const [sending, setSending] = useState(false);

    const [showNewConvDialog, setShowNewConvDialog] = useState(false);
    const [mobilePane, setMobilePane] = useState('list'); // 'list' | 'chat'

    // Refs for scroll handling
    const messagesContainerRef = useRef(null);
    const convListContainerRef = useRef(null);
    const composerFileInputRef = useRef(null);
    const handledProfileIdRef = useRef(false);

    // ── Mobile detection ────────────────────────────────────────────────
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const apply = () => setIsMobile(mq.matches);
        apply();
        if (mq.addEventListener) mq.addEventListener('change', apply);
        else mq.addListener(apply);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', apply);
            else mq.removeListener(apply);
        };
    }, []);

    // ── Chrome-measured shell height (P23) ─────────────────────────────
    const shellRef = useRef(null);
    const [shellHeight, setShellHeight] = useState('80vh');
    const mobileChatTakeover = isMobile && mobilePane === 'chat';
    useLayoutEffect(() => {
        const measure = () => {
            if (!shellRef.current) return;
            const top = shellRef.current.getBoundingClientRect().top;
            const selectors = ['.mobile-bottom-nav', '.layout-footer', '.layout-main-footer', 'footer'];
            let bottomEdge = window.innerHeight;
            for (const sel of selectors) {
                const el = document.querySelector(sel);
                if (!el) continue;
                const rect = el.getBoundingClientRect();
                if (rect.height === 0 && rect.width === 0) continue;
                if (rect.top <= top) continue;
                if (rect.top < bottomEdge) bottomEdge = rect.top;
            }
            // No gutter — shell touches the footer edge directly. Saves
            // every available pixel between topbar and footer (matches the
            // WhatsApp Web / Telegram Web full-viewport chat pattern).
            const next = Math.max(bottomEdge - top, 320);
            setShellHeight(`${next}px`);
        };
        measure();
        const raf = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('resize', measure);
            cancelAnimationFrame(raf);
        };
    }, [mobileChatTakeover, isMobile]);

    // ── Load conversations (initial + silent refresh) ──────────────────
    const loadConversations = useCallback(async ({ silent = false, page = 1 } = {}) => {
        if (!silent) setConvsLoading(true);
        try {
            const response = await messagingService.getConversations({ per_page: CONV_PAGE_SIZE, page });
            if (response.success) {
                const payload = response.data?.data || response.data || [];
                const lastPage = response.data?.last_page;
                const currPage = response.data?.current_page || page;
                const list = Array.isArray(payload) ? payload : [];
                if (page === 1) setConversations(list);
                else setConversations((prev) => [...prev, ...list]);
                setConvsPage(currPage);
                setConvsHasMore(lastPage ? currPage < lastPage : false);
            }
        } catch (err) {
            if (!silent) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Failed to load conversations',
                    life: 3000,
                });
            }
        } finally {
            if (!silent) setConvsLoading(false);
        }
    }, []);

    const loadMoreConversations = useCallback(async () => {
        if (convsLoadingMore || !convsHasMore) return;
        setConvsLoadingMore(true);
        try {
            const response = await messagingService.getConversations({
                per_page: CONV_PAGE_SIZE,
                page: convsPage + 1,
            });
            if (response.success) {
                const payload = response.data?.data || response.data || [];
                const lastPage = response.data?.last_page;
                const currPage = response.data?.current_page || convsPage + 1;
                setConversations((prev) => [...prev, ...(Array.isArray(payload) ? payload : [])]);
                setConvsPage(currPage);
                setConvsHasMore(lastPage ? currPage < lastPage : false);
            }
        } catch (err) {
            // silent
        } finally {
            setConvsLoadingMore(false);
        }
    }, [convsLoadingMore, convsHasMore, convsPage]);

    // ── Load messages for the active conversation (initial + silent) ──
    const loadMessages = useCallback(async (convId, { silent = false, page = 1 } = {}) => {
        if (!convId) return;
        if (!silent) setMessagesLoading(true);
        try {
            const response = await messagingService.getMessages(convId, { per_page: MSG_PAGE_SIZE, page });
            if (response.success) {
                const payload = response.data?.data || response.data || [];
                const lastPage = response.data?.last_page;
                const currPage = response.data?.current_page || page;
                // Backend returns newest-first or oldest-first? Defensive — sort
                // ascending by created_at so render order matches a chat client.
                const list = Array.isArray(payload) ? [...payload] : [];
                list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                if (page === 1) {
                    setMessagesByConv((prev) => ({ ...prev, [convId]: list }));
                } else {
                    setMessagesByConv((prev) => {
                        const existing = prev[convId] || [];
                        const ids = new Set(existing.map((m) => m.id));
                        const merged = [...list.filter((m) => !ids.has(m.id)), ...existing];
                        return { ...prev, [convId]: merged };
                    });
                }
                setOlderHasMoreByConv((prev) => ({
                    ...prev,
                    [convId]: lastPage ? currPage < lastPage : false,
                }));
            }
        } catch (err) {
            if (!silent) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Failed to load messages',
                    life: 3000,
                });
            }
        } finally {
            if (!silent) setMessagesLoading(false);
        }
    }, []);

    // Lazy load older messages — preserve scroll position.
    const loadOlderMessages = useCallback(async () => {
        if (!activeConvId || olderLoading || !olderHasMoreByConv[activeConvId]) return;
        const container = messagesContainerRef.current;
        const oldHeight = container?.scrollHeight ?? 0;
        const oldScroll = container?.scrollTop ?? 0;

        const existingMessages = messagesByConv[activeConvId] || [];
        // We need next page — backend may return newer→older or older→newer.
        // Easiest: count what we have, ask for page = ceil(count / size) + 1.
        const nextPage = Math.floor(existingMessages.length / MSG_PAGE_SIZE) + 1 + 1;

        setOlderLoading(true);
        try {
            await loadMessages(activeConvId, { silent: true, page: nextPage });
            // Restore scroll position so user's reading anchor doesn't jump
            requestAnimationFrame(() => {
                if (container) {
                    container.scrollTop = oldScroll + (container.scrollHeight - oldHeight);
                }
            });
        } finally {
            setOlderLoading(false);
        }
    }, [activeConvId, olderLoading, olderHasMoreByConv, messagesByConv, loadMessages]);

    // ── Initial mount: load conversations + register silent refresh ────
    useEffect(() => {
        loadConversations({ silent: false, page: 1 });
        fetchUnreadCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        registerConversationListCallback(() => loadConversations({ silent: true, page: 1 }));
        return () => registerConversationListCallback(null);
    }, [registerConversationListCallback, loadConversations]);

    // ── Polling: conversation list (10 s, visibility-aware) ────────────
    useEffect(() => {
        const tick = () => {
            if (document.visibilityState === 'visible') {
                loadConversations({ silent: true, page: 1 });
            }
        };
        const id = setInterval(tick, POLL_LIST_MS);
        const onVis = () => {
            if (document.visibilityState === 'visible') tick();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [loadConversations]);

    // ── Polling: active chat (5 s, visibility-aware) ──────────────────
    useEffect(() => {
        if (!activeConvId) return undefined;
        const tick = () => {
            if (document.visibilityState !== 'visible') return;
            // Just refetch page 1; merge by id so we never duplicate
            messagingService.getMessages(activeConvId, { per_page: MSG_PAGE_SIZE, page: 1 })
                .then((res) => {
                    if (!res.success) return;
                    const payload = res.data?.data || res.data || [];
                    const list = Array.isArray(payload) ? [...payload] : [];
                    list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    setMessagesByConv((prev) => {
                        const existing = prev[activeConvId] || [];
                        const ids = new Set(existing.map((m) => m.id));
                        const fresh = list.filter((m) => !ids.has(m.id));
                        if (fresh.length === 0) return prev;
                        const container = messagesContainerRef.current;
                        const wasNearBottom = container
                            ? container.scrollHeight - container.scrollTop - container.clientHeight < 100
                            : true;
                        if (!wasNearBottom) {
                            setNewMessagesPill((c) => c + fresh.length);
                        }
                        return { ...prev, [activeConvId]: [...existing, ...fresh] };
                    });
                })
                .catch(() => {});
        };
        const id = setInterval(tick, POLL_CHAT_MS);
        return () => clearInterval(id);
    }, [activeConvId]);

    // ── Conversation open: load messages + mark read ───────────────────
    const openConversation = useCallback((convId, { fromList = true } = {}) => {
        if (!convId) return;
        setActiveConvId(convId);
        if (fromList && isMobile) setMobilePane('chat');
        setNewMessagesPill(0);

        // Mark read — optimistic
        const conv = conversations.find((c) => c.id === convId);
        if (conv && (conv.unread_count || 0) > 0) {
            const had = conv.unread_count;
            setConversations((prev) =>
                prev.map((c) => (c.id === convId ? { ...c, unread_count: 0 } : c))
            );
            setUnreadCount((prev) => Math.max(0, prev - had));
            messagingService.markAsRead(convId).catch(() => {
                // non-fatal — next poll will sync back if it fails
            });
        }

        // If we don't have messages yet, fetch them; otherwise just scroll to bottom
        if (!messagesByConv[convId]) {
            loadMessages(convId, { silent: false, page: 1 });
        }
    }, [isMobile, conversations, messagesByConv, loadMessages, setUnreadCount]);

    // Auto-scroll to bottom on conv switch / new message (when near bottom)
    useEffect(() => {
        if (!activeConvId) return;
        const list = messagesByConv[activeConvId] || [];
        if (list.length === 0) return;
        const container = messagesContainerRef.current;
        if (!container) return;
        const wasNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        // Always scroll on initial open (the load just resolved)
        if (wasNearBottom) {
            requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight;
            });
        }
    }, [activeConvId, messagesByConv]);

    // ── Route param /:convId (deep link to a specific conv) ───────────
    useEffect(() => {
        if (!routeConvId) return;
        const idNum = Number(routeConvId);
        if (Number.isNaN(idNum)) return;
        if (conversations.some((c) => c.id === idNum)) {
            openConversation(idNum, { fromList: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeConvId, conversations.length]);

    // ── Query param ?profileId=NNN (deep link to a connection) ────────
    useEffect(() => {
        if (handledProfileIdRef.current) return;
        if (convsLoading) return; // wait for the list
        const raw = searchParams.get('profileId');
        if (!raw) return;
        handledProfileIdRef.current = true;

        const pid = Number(raw);
        const existing = conversations.find(
            (c) => Number(c.other_participant?.profile_id) === pid
        );
        if (existing) {
            openConversation(existing.id, { fromList: false });
        } else {
            messagingService.createConversation(pid).then((res) => {
                if (res.success) {
                    const newConv = res.data;
                    setConversations((prev) => [newConv, ...prev]);
                    openConversation(newConv.id, { fromList: false });
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Conversation started',
                        life: 1500,
                    });
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: res.message || 'Failed to start conversation',
                        life: 2500,
                    });
                }
            });
        }
        // Clear the param so refresh doesn't retrigger
        const next = new URLSearchParams(searchParams);
        next.delete('profileId');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convsLoading, conversations.length]);

    // ── Send message ───────────────────────────────────────────────────
    const handleSend = async () => {
        if (!activeConvId) return;
        const body = composerValue.trim();
        if (!body && !pendingFile) return;

        // Optimistic: append to local state with pending: true
        const tempId = `tmp-${Date.now()}`;
        const optimistic = {
            id: tempId,
            body,
            is_mine: true,
            created_at: new Date().toISOString(),
            read: false,
            file: pendingFile ? { name: pendingFile.name, size_display: formatBytes(pendingFile.size), type: pendingFile.type } : null,
            __pending: true,
        };
        setMessagesByConv((prev) => ({
            ...prev,
            [activeConvId]: [...(prev[activeConvId] || []), optimistic],
        }));
        // Reorder conv to top + update last_message preview
        setConversations((prev) => {
            const updated = prev.map((c) =>
                c.id === activeConvId
                    ? {
                          ...c,
                          last_message: { body, is_mine: true, sender_profile_id: null, created_at: optimistic.created_at },
                          last_message_at: optimistic.created_at,
                      }
                    : c
            );
            const active = updated.find((c) => c.id === activeConvId);
            return active ? [active, ...updated.filter((c) => c.id !== activeConvId)] : updated;
        });

        const fileToSend = pendingFile;
        setComposerValue('');
        setPendingFile(null);
        setSending(true);
        try {
            const res = await messagingService.sendMessage(activeConvId, body, fileToSend);
            if (res.success) {
                const real = res.data;
                setMessagesByConv((prev) => ({
                    ...prev,
                    [activeConvId]: (prev[activeConvId] || []).map((m) =>
                        m.id === tempId ? { ...real, is_mine: true } : m
                    ),
                }));
                // Auto-scroll to bottom (we just sent something, user expects to see it)
                requestAnimationFrame(() => {
                    if (messagesContainerRef.current) {
                        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                    }
                });
            } else {
                throw new Error(res.message || 'Send failed');
            }
        } catch (err) {
            // Mark optimistic message as failed
            setMessagesByConv((prev) => ({
                ...prev,
                [activeConvId]: (prev[activeConvId] || []).map((m) =>
                    m.id === tempId ? { ...m, __pending: false, __failed: true } : m
                ),
            }));
            toast.current?.show({ severity: 'error', summary: 'Send failed', life: 3000 });
        } finally {
            setSending(false);
        }
    };

    // ── Delete conversation ────────────────────────────────────────────
    const handleDeleteConversation = (convId) => {
        const conv = conversations.find((c) => c.id === convId);
        confirmDialog({
            message: `Delete conversation with ${conv?.other_participant?.full_name || 'this person'}? This cannot be undone.`,
            header: 'Delete conversation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const res = await messagingService.deleteConversation(convId);
                    if (res.success) {
                        setConversations((prev) => prev.filter((c) => c.id !== convId));
                        setMessagesByConv((prev) => {
                            const next = { ...prev };
                            delete next[convId];
                            return next;
                        });
                        if (activeConvId === convId) {
                            setActiveConvId(null);
                            if (isMobile) setMobilePane('list');
                        }
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Conversation deleted',
                            life: 1500,
                        });
                    } else {
                        throw new Error(res.message || 'Delete failed');
                    }
                } catch (err) {
                    toast.current?.show({ severity: 'error', summary: 'Delete failed', life: 3000 });
                }
            },
        });
    };

    // ── Conv-list lazy load on scroll ──────────────────────────────────
    const handleConvListScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
            loadMoreConversations();
        }
    };

    // ── Messages lazy load (older) on scroll-up ───────────────────────
    const handleMessagesScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollTop < 50 && olderHasMoreByConv[activeConvId] && !olderLoading) {
            loadOlderMessages();
        }
        // Hide "↓ N new" pill if user scrolls down to bottom
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
        if (nearBottom && newMessagesPill > 0) setNewMessagesPill(0);
    };

    const scrollToBottom = () => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
        setNewMessagesPill(0);
    };

    // ── New conversation from connections picker ──────────────────────
    const handleNewConversation = async (connection) => {
        const pid = Number(connection.other_party?.profile_id || connection.profile_id);
        if (!pid) return;
        const existing = conversations.find(
            (c) => Number(c.other_participant?.profile_id) === pid
        );
        if (existing) {
            openConversation(existing.id, { fromList: false });
            setShowNewConvDialog(false);
            return;
        }
        try {
            const res = await messagingService.createConversation(pid);
            if (res.success) {
                const newConv = res.data;
                setConversations((prev) => [newConv, ...prev]);
                openConversation(newConv.id, { fromList: false });
                setShowNewConvDialog(false);
                toast.current?.show({
                    severity: 'success',
                    summary: `New conversation with ${(newConv.other_participant?.full_name || '').split(' ')[0]}`,
                    life: 1500,
                });
            } else {
                throw new Error(res.message || 'Failed');
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Failed to start conversation', life: 3000 });
        }
    };

    // ── File picker ────────────────────────────────────────────────────
    const handleAttachClick = () => composerFileInputRef.current?.click();
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.current?.show({ severity: 'error', summary: 'File too large (max 5 MB)', life: 3000 });
            return;
        }
        setPendingFile(file);
        if (composerFileInputRef.current) composerFileInputRef.current.value = '';
    };

    // ── Filtered conv list ────────────────────────────────────────────
    const filteredConvs = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return conversations;
        return conversations.filter((c) => {
            const p = c.other_participant || {};
            return (
                (p.full_name || '').toLowerCase().includes(q) ||
                (c.last_message?.body || '').toLowerCase().includes(q)
            );
        });
    }, [conversations, search]);

    const activeConv = useMemo(
        () => conversations.find((c) => c.id === activeConvId) || null,
        [conversations, activeConvId]
    );
    const activeMessages = activeConvId ? (messagesByConv[activeConvId] || []) : [];

    // Group messages by author + insert day dividers
    const groupedMessages = useMemo(() => {
        const result = [];
        let lastDay = null;
        let lastAuthorKey = null;
        activeMessages.forEach((m, idx) => {
            const day = dayLabel(m.created_at);
            if (day && day !== lastDay) {
                result.push({ kind: 'day', key: `day-${idx}`, label: day });
                lastDay = day;
                lastAuthorKey = null;
            }
            const authorKey = m.is_mine ? 'me' : 'them';
            const isFirst = lastAuthorKey !== authorKey;
            const next = activeMessages[idx + 1];
            const nextDay = next ? dayLabel(next.created_at) : null;
            const isLast = !next || (next.is_mine ? 'me' : 'them') !== authorKey || (nextDay && nextDay !== day);
            result.push({ kind: 'msg', key: `m-${m.id}`, message: m, isFirst, isLast });
            lastAuthorKey = authorKey;
        });
        return result;
    }, [activeMessages]);

    // ── Render flags ───────────────────────────────────────────────────
    const showList = !isMobile || mobilePane === 'list';
    const showChat = !isMobile || mobilePane === 'chat';

    return (
        // Bare wrapper (no PrimeFlex .grid / .col-12) — those add ~1rem of
        // padding/margin around children that we don't want here. The shell
        // hugs the topbar with zero gap.
        <div>
            <Toast ref={toast} position="top-right" />
            <ConfirmDialog />

            {/* No page-level "Messages" h1 — the topbar breadcrumb already
                identifies the page. Dropping it + the grid/col padding
                lets the shell claim every pixel between topbar and footer. */}

            {/* Shell — bordered card on desktop / mobile-list, full-bleed on mobile chat. */}
            <div>
                <div
                    ref={shellRef}
                    className={
                        mobileChatTakeover
                            ? 'surface-card overflow-hidden'
                            : 'surface-card border-round-xl shadow-1 overflow-hidden surface-border border-1'
                    }
                    style={{ height: shellHeight }}
                >
                    <div className="flex h-full">
                        {showList && (
                            <div
                                className="h-full flex flex-column flex-shrink-0 surface-50"
                                style={{
                                    width: isMobile ? '100%' : '320px',
                                    borderRight: isMobile ? 'none' : '1px solid var(--surface-border)',
                                    minHeight: 0,
                                }}
                            >
                                <ConversationListPanel
                                    conversations={filteredConvs}
                                    loading={convsLoading}
                                    activeId={activeConvId}
                                    search={search}
                                    onSearchChange={setSearch}
                                    onSelect={(id) => openConversation(id, { fromList: true })}
                                    onNew={() => setShowNewConvDialog(true)}
                                    onDelete={handleDeleteConversation}
                                    onScroll={handleConvListScroll}
                                    listRef={convListContainerRef}
                                    loadingMore={convsLoadingMore}
                                />
                            </div>
                        )}
                        {showChat && (
                            <div className="flex-1 h-full flex flex-column" style={{ minWidth: 0, minHeight: 0 }}>
                                {activeConv ? (
                                    <ChatPanel
                                        conversation={activeConv}
                                        groupedMessages={groupedMessages}
                                        messagesLoading={messagesLoading}
                                        olderLoading={olderLoading}
                                        onBack={isMobile ? () => setMobilePane('list') : null}
                                        onClose={!isMobile ? () => setActiveConvId(null) : null}
                                        onDelete={() => handleDeleteConversation(activeConv.id)}
                                        composerValue={composerValue}
                                        onComposerChange={setComposerValue}
                                        onSend={handleSend}
                                        sending={sending}
                                        pendingFile={pendingFile}
                                        onAttachClick={handleAttachClick}
                                        onClearAttachment={() => setPendingFile(null)}
                                        isMobile={isMobile}
                                        messagesContainerRef={messagesContainerRef}
                                        onMessagesScroll={handleMessagesScroll}
                                        newMessagesPill={newMessagesPill}
                                        onScrollToBottom={scrollToBottom}
                                    />
                                ) : (
                                    <EmptyChatState onNew={() => setShowNewConvDialog(true)} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <input
                ref={composerFileInputRef}
                type="file"
                accept=".pdf,.csv,.txt,.xlsx,.xls,image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            <NewConversationDialog
                visible={showNewConvDialog}
                onHide={() => setShowNewConvDialog(false)}
                onSelect={handleNewConversation}
            />

            {/* Page-scoped CSS */}
            <style>{`
                .typing-dot {
                    width: 0.4rem;
                    height: 0.4rem;
                    border-radius: 999px;
                    background-color: var(--text-color-secondary);
                    animation: typing-bounce 1.1s infinite;
                    display: inline-block;
                }
                @keyframes typing-bounce {
                    0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
                    30%           { transform: translateY(-3px); opacity: 1; }
                }
                .conv-row { position: relative; }
                .conv-row .conv-row-menu { opacity: 0; transition: opacity 150ms ease; }
                .conv-row:hover .conv-row-menu,
                .conv-row:focus-within .conv-row-menu { opacity: 1; }
                @media (max-width: 767px) {
                    .conv-row .conv-row-menu { opacity: 1; }
                    .layout-footer { display: none !important; }
                }
                .messaging-composer .p-inputtextarea {
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;
                    background: transparent !important;
                    resize: none;
                    padding: 0.55rem 0.25rem;
                    font-size: 0.95rem;
                    line-height: 1.35;
                    width: 100%;
                    max-height: 7rem;
                    overflow-y: auto;
                }
                .messaging-composer .p-inputtextarea:focus { box-shadow: none !important; }
            `}</style>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
// LEFT PANE — conversation list
// ═════════════════════════════════════════════════════════════════════════
const ConversationListPanel = ({
    conversations, loading, activeId, search, onSearchChange,
    onSelect, onNew, onDelete,
    onScroll, listRef, loadingMore,
}) => (
    <>
        <div className="surface-0 surface-border border-bottom-1" style={{ flexShrink: 0 }}>
            <div className="flex align-items-center justify-content-between gap-2" style={{ padding: '0.75rem 0.875rem' }}>
                <h2 className="text-900 font-semibold text-base m-0">Chats</h2>
                <Button
                    icon="pi pi-pencil"
                    text
                    rounded
                    aria-label="New message"
                    tooltip="New message"
                    tooltipOptions={{ position: 'left' }}
                    onClick={onNew}
                    pt={{ root: { style: { width: '2.25rem', height: '2.25rem' } } }}
                />
            </div>
            <div style={{ padding: '0 0.875rem 0.625rem' }}>
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search or start new chat"
                        className="w-full p-inputtext-sm"
                        style={{ borderRadius: '999px', backgroundColor: 'var(--surface-100)', border: 'none' }}
                    />
                </IconField>
            </div>
        </div>

        <div
            ref={listRef}
            className="flex-1 overflow-y-auto"
            style={{ minHeight: 0 }}
            onScroll={onScroll}
        >
            {loading && conversations.length === 0 ? (
                <div className="p-3 flex flex-column gap-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex gap-2 align-items-center">
                            <Skeleton shape="circle" size="2.75rem" />
                            <div className="flex-1">
                                <Skeleton width="60%" height="0.85rem" className="mb-2" />
                                <Skeleton width="80%" height="0.7rem" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : conversations.length === 0 ? (
                <EmptyConvListState searchActive={!!search} onNew={onNew} />
            ) : (
                <ul className="list-none p-0 m-0">
                    {conversations.map((c, idx) => (
                        <li key={c.id} className="conv-row">
                            <ConversationListItem
                                conversation={c}
                                active={c.id === activeId}
                                onClick={() => onSelect(c.id)}
                                onDelete={() => onDelete(c.id)}
                            />
                            {idx < conversations.length - 1 && (
                                <div className="surface-border border-top-1" style={{ marginLeft: '4rem' }} />
                            )}
                        </li>
                    ))}
                    {loadingMore && (
                        <div className="p-3 text-center text-500 text-xs">Loading more…</div>
                    )}
                </ul>
            )}
        </div>
    </>
);

const ConversationListItem = ({ conversation, active, onClick, onDelete }) => {
    const p = conversation.other_participant || {};
    const last = conversation.last_message;
    const hasUnread = (conversation.unread_count || 0) > 0;
    const menuRef = useRef(null);
    const color = colorFor(p.profile_id);

    const menuItems = [
        { label: 'Delete conversation', icon: 'pi pi-trash', className: 'text-red-600', command: onDelete },
    ];

    return (
        <div
            onClick={onClick}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
            role="button"
            tabIndex={0}
            aria-current={active ? 'true' : undefined}
            className={`flex align-items-start gap-2 p-3 cursor-pointer transition-colors transition-duration-150 ${
                active ? 'surface-100' : 'surface-0 hover:surface-50'
            }`}
        >
            <div className="flex-shrink-0">
                <Avatar
                    label={initialsFrom(p.full_name)}
                    image={photoUrlFor(p)}
                    shape="circle"
                    style={{
                        width: '2.75rem',
                        height: '2.75rem',
                        backgroundColor: `var(--${color}-100)`,
                        color: `var(--${color}-700)`,
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        border: '2px solid var(--surface-0)',
                        boxShadow: `0 0 0 2px var(--${color}-200)`,
                    }}
                />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex align-items-center justify-content-between gap-2">
                    <span className={`text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis ${
                        hasUnread ? 'text-900 font-bold' : 'text-900 font-medium'
                    }`}>
                        {p.full_name || 'Unknown'}
                    </span>
                    <span className={`text-xs flex-shrink-0 ${hasUnread ? 'text-primary font-semibold' : 'text-500'}`}>
                        {last ? timeAgo(last.created_at || conversation.last_message_at) : ''}
                    </span>
                </div>
                <div className="flex align-items-center justify-content-between gap-2 mt-1">
                    <span className={`text-xs line-height-3 white-space-nowrap overflow-hidden text-overflow-ellipsis flex-1 ${
                        hasUnread ? 'text-700 font-medium' : 'text-500'
                    }`}>
                        {last ? (
                            <>
                                {last.is_mine && <span className="text-500">You: </span>}
                                {(last.body || (last.file ? '[Attachment]' : '')).replace(/\s*\n+\s*/g, ' ')}
                            </>
                        ) : (
                            <span className="text-500 font-italic">No messages yet</span>
                        )}
                    </span>
                    {hasUnread && (
                        <Badge value={conversation.unread_count} severity="info" className="flex-shrink-0" />
                    )}
                </div>
            </div>

            <div className="conv-row-menu flex-shrink-0">
                <Menu model={menuItems} popup ref={menuRef} />
                <Button
                    icon="pi pi-ellipsis-v"
                    text rounded size="small"
                    aria-label="Conversation actions"
                    onClick={(e) => { e.stopPropagation(); menuRef.current.toggle(e); }}
                    pt={{ root: { style: { width: '1.75rem', height: '1.75rem' } } }}
                />
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
// RIGHT PANE — chat
// ═════════════════════════════════════════════════════════════════════════
const ChatPanel = ({
    conversation, groupedMessages, messagesLoading, olderLoading,
    onBack, onClose, onDelete,
    composerValue, onComposerChange, onSend, sending,
    pendingFile, onAttachClick, onClearAttachment,
    isMobile,
    messagesContainerRef, onMessagesScroll,
    newMessagesPill, onScrollToBottom,
}) => {
    const headerMenuRef = useRef(null);
    const navigate = useNavigate();
    const p = conversation.other_participant || {};
    const color = colorFor(p.profile_id);

    const goToProfile = () => {
        if (p.profile_id) navigate(`/app/profile/${p.profile_id}`);
    };

    const headerMenuItems = [
        // "Close chat" — closes the currently open chat panel and returns
        // the user to the conversation list. Stops the 8s active-chat poll
        // so we don't burn requests on a chat the user isn't watching.
        ...(onClose || onBack ? [{
            label: 'Close chat',
            icon: 'pi pi-times',
            command: () => (onClose || onBack)(),
        }] : []),
        ...(onClose || onBack ? [{ separator: true }] : []),
        { label: 'Delete conversation', icon: 'pi pi-trash', className: 'text-red-600', command: onDelete },
    ];

    return (
        <>
            {/* Sticky header */}
            <div
                className="flex align-items-center gap-2 surface-0 surface-border border-bottom-1"
                style={{ flexShrink: 0, padding: '0.5rem 0.625rem' }}
            >
                {onBack && (
                    <Button
                        icon="pi pi-arrow-left"
                        text rounded
                        onClick={onBack}
                        aria-label="Back to conversations"
                        pt={{ root: { style: { width: '2.25rem', height: '2.25rem' } } }}
                    />
                )}
                {/* Avatar + identity → clickable to view profile (whole
                    block, like WhatsApp / Telegram). The 3-dot menu and
                    close-chat button on the right are NOT inside this
                    click target so they keep their own behaviour. */}
                <div
                    className="flex align-items-center gap-2 flex-1 min-w-0 cursor-pointer border-round transition-colors transition-duration-150 hover:surface-50"
                    role="button"
                    tabIndex={0}
                    onClick={goToProfile}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goToProfile();
                        }
                    }}
                    aria-label={`View ${p.full_name || 'this person'}'s profile`}
                    style={{ padding: '0.25rem 0.4rem' }}
                >
                    <Avatar
                        label={initialsFrom(p.full_name)}
                        image={photoUrlFor(p)}
                        shape="circle"
                        style={{
                            width: '2.25rem',
                            height: '2.25rem',
                            backgroundColor: `var(--${color}-100)`,
                            color: `var(--${color}-700)`,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            flexShrink: 0,
                        }}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {p.full_name || 'Unknown'}
                        </div>
                        {p.headline && (
                            <div className="text-500 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis line-height-2">
                                {p.headline}
                            </div>
                        )}
                    </div>
                </div>
                <Menu model={headerMenuItems} popup ref={headerMenuRef} />
                <Button
                    icon="pi pi-ellipsis-v"
                    text rounded
                    aria-label="More"
                    onClick={(e) => headerMenuRef.current.toggle(e)}
                    pt={{ root: { style: { width: '2.25rem', height: '2.25rem' } } }}
                />
                {/* Close chat — desktop only. Pauses the 8s active-chat poll
                    until the user opens a conversation again (WhatsApp Web
                    pattern). Mobile uses the back button instead. */}
                {onClose && (
                    <Button
                        icon="pi pi-times"
                        text rounded
                        aria-label="Close chat"
                        tooltip="Close chat"
                        tooltipOptions={{ position: 'left' }}
                        onClick={onClose}
                        pt={{ root: { style: { width: '2.25rem', height: '2.25rem' } } }}
                    />
                )}
            </div>

            {/* Messages stream */}
            <div
                ref={messagesContainerRef}
                onScroll={onMessagesScroll}
                className="flex-1 overflow-y-auto p-3 surface-50 relative"
                style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
                {olderLoading && (
                    <div className="text-500 text-xs text-center py-2">Loading older messages…</div>
                )}
                {messagesLoading && groupedMessages.length === 0 ? (
                    <div className="flex-1 flex align-items-center justify-content-center">
                        <div className="text-500 text-sm">Loading messages…</div>
                    </div>
                ) : groupedMessages.length === 0 ? (
                    <div className="flex-1 flex align-items-center justify-content-center">
                        <div className="text-center" style={{ maxWidth: '20rem' }}>
                            <i className="pi pi-comments text-400" style={{ fontSize: '2rem' }} aria-hidden="true" />
                            <p className="text-600 text-sm mt-2 mb-0">
                                Say hi to {(p.full_name || '').split(' ')[0] || 'them'} — they'll see your message here.
                            </p>
                        </div>
                    </div>
                ) : (
                    groupedMessages.map((g) => {
                        if (g.kind === 'day') return <DayDivider key={g.key} label={g.label} />;
                        return (
                            <MessageBubble
                                key={g.key}
                                message={g.message}
                                isFirst={g.isFirst}
                                isLast={g.isLast}
                            />
                        );
                    })
                )}

                {/* "↓ N new messages" pill */}
                {newMessagesPill > 0 && (
                    <Button
                        label={`${newMessagesPill} new message${newMessagesPill > 1 ? 's' : ''}`}
                        icon="pi pi-arrow-down"
                        size="small"
                        rounded
                        onClick={onScrollToBottom}
                        style={{
                            position: 'sticky',
                            bottom: '0.75rem',
                            alignSelf: 'center',
                            zIndex: 5,
                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
                        }}
                    />
                )}
            </div>

            {/* Sticky composer */}
            <ChatComposer
                value={composerValue}
                onChange={onComposerChange}
                onSend={onSend}
                sending={sending}
                pendingFile={pendingFile}
                onAttachClick={onAttachClick}
                onClearAttachment={onClearAttachment}
                isMobile={isMobile}
            />
        </>
    );
};

// ─── Day divider ────────────────────────────────────────────────────────
const DayDivider = ({ label }) => (
    <div className="flex align-items-center gap-2 my-3">
        <div className="flex-1 surface-border border-top-1" />
        <span
            className="text-500 text-xs font-medium px-2 py-1 surface-0 border-round-xl"
            style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
        >
            {label}
        </span>
        <div className="flex-1 surface-border border-top-1" />
    </div>
);

// ─── Message bubble ─────────────────────────────────────────────────────
// 1-on-1 chat — no per-bubble avatar (the chat header already identifies
// who you're talking to). Bubbles align mine→right / theirs→left, with
// tail-corner squaring on grouped messages.
const MessageBubble = ({ message, isFirst, isLast }) => {
    const mine = !!message.is_mine;
    const file = message.file;
    const isPending = !!message.__pending;
    const isFailed = !!message.__failed;
    const isRead = message.read || message.is_read || !!message.read_at;

    const bubbleBaseStyle = {
        padding: '0.55rem 0.85rem',
        borderRadius: '1rem',
        maxWidth: '100%',
        wordBreak: 'break-word',
        boxShadow: mine ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
        opacity: isPending ? 0.6 : 1,
    };
    const tail = '0.35rem';
    if (mine) {
        if (!isLast)  bubbleBaseStyle.borderBottomRightRadius = tail;
        if (!isFirst) bubbleBaseStyle.borderTopRightRadius    = tail;
    } else {
        if (!isLast)  bubbleBaseStyle.borderBottomLeftRadius = tail;
        if (!isFirst) bubbleBaseStyle.borderTopLeftRadius    = tail;
    }
    const bubbleStyle = mine
        ? { ...bubbleBaseStyle, backgroundColor: 'var(--primary-color)', color: 'var(--primary-color-text)' }
        : { ...bubbleBaseStyle, backgroundColor: 'var(--surface-0)', color: 'var(--text-color)' };

    return (
        <div
            className={`flex ${mine ? 'justify-content-end' : 'justify-content-start'}`}
            style={{ marginTop: isFirst ? '0.5rem' : '0.15rem' }}
        >
            <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                {file ? (
                    <FileBubble file={file} mine={mine} bubbleStyle={bubbleStyle} />
                ) : (
                    <div style={bubbleStyle}>
                        <p
                            className="m-0 text-sm line-height-3"
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                            {message.body}
                        </p>
                    </div>
                )}
                {isLast && (
                    <div className={`text-400 text-xs mt-1 flex align-items-center gap-1 ${mine ? 'justify-content-end' : ''}`}>
                        {isFailed ? (
                            <span className="text-red-500">
                                <i className="pi pi-exclamation-circle" style={{ fontSize: '0.65rem' }} /> Failed
                            </span>
                        ) : isPending ? (
                            <span className="text-500">Sending…</span>
                        ) : (
                            <>
                                <span>{timeAgo(message.created_at)}</span>
                                {mine && (
                                    <i
                                        className={`pi ${isRead ? 'pi-check-circle' : 'pi-check'}`}
                                        style={{
                                            fontSize: '0.7rem',
                                            color: isRead ? 'var(--primary-color)' : 'var(--text-color-secondary)',
                                        }}
                                        aria-label={isRead ? 'Read' : 'Sent'}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const FileBubble = ({ file, mine, bubbleStyle }) => {
    const iconByType = { pdf: 'pi-file-pdf', image: 'pi-image', doc: 'pi-file-word', default: 'pi-file' };
    const detectedType = (file.type || '').includes('pdf') ? 'pdf'
        : (file.type || '').startsWith('image/') ? 'image'
        : 'default';
    const icon = iconByType[detectedType] || iconByType.default;
    return (
        <div
            className={`flex align-items-center gap-2 ${file.url ? 'cursor-pointer' : ''}`}
            style={{ ...bubbleStyle, padding: '0.6rem 0.85rem' }}
            onClick={() => file.url && window.open(file.url, '_blank', 'noopener')}
        >
            <div
                className="flex align-items-center justify-content-center border-round-xl flex-shrink-0"
                style={{
                    width: '2rem', height: '2rem',
                    backgroundColor: mine ? 'rgba(255,255,255,0.2)' : 'var(--surface-100)',
                }}
            >
                <i className={`pi ${icon}`} style={{ fontSize: '1rem' }} aria-hidden="true" />
            </div>
            <div className="min-w-0">
                <div className="text-sm font-semibold white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ maxWidth: '12rem' }}>
                    {file.name}
                </div>
                <div className="text-xs" style={{ opacity: 0.75 }}>{file.size_display || ''}</div>
            </div>
        </div>
    );
};

// ─── Composer ───────────────────────────────────────────────────────────
const QUICK_EMOJIS = ['👍', '🙏', '🎉', '🔥', '💯', '✅', '😊', '😂', '❤️', '👏'];

const ChatComposer = ({
    value, onChange, onSend, sending,
    pendingFile, onAttachClick, onClearAttachment,
    isMobile,
}) => {
    const textareaRef = useRef(null);
    const emojiRef = useRef(null);

    const handleKeyDown = (e) => {
        if (isMobile) return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    const insertEmoji = (emoji, e) => {
        onChange((value || '') + emoji);
        emojiRef.current?.hide(e);
        textareaRef.current?.focus();
    };

    const hasContent = !!value.trim() || !!pendingFile;

    return (
        <div className="surface-0 surface-border border-top-1 messaging-composer" style={{ flexShrink: 0 }}>
            {/* Pending-file preview chip */}
            {pendingFile && (
                <div className="flex align-items-center gap-2 px-3 py-2 surface-100 surface-border border-bottom-1">
                    <i className="pi pi-paperclip text-primary" style={{ fontSize: '0.85rem' }} />
                    <span className="text-700 text-xs flex-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {pendingFile.name}
                    </span>
                    <span className="text-500 text-xs">{formatBytes(pendingFile.size)}</span>
                    <Button
                        icon="pi pi-times"
                        text rounded size="small"
                        aria-label="Remove attachment"
                        onClick={onClearAttachment}
                        pt={{ root: { style: { width: '1.75rem', height: '1.75rem' } } }}
                    />
                </div>
            )}

            <div className="flex align-items-end gap-2" style={{ padding: '0.5rem 0.625rem' }}>
                <div
                    className="flex-1 flex align-items-end"
                    style={{
                        backgroundColor: 'var(--surface-100)',
                        borderRadius: '1.25rem',
                        paddingLeft: '0.25rem',
                        paddingRight: '0.25rem',
                    }}
                >
                    <Button
                        icon="pi pi-face-smile"
                        text rounded
                        aria-label="Emoji"
                        onClick={(e) => emojiRef.current.toggle(e)}
                        pt={{ root: { style: { width: '2.25rem', height: '2.25rem', flexShrink: 0, color: 'var(--text-color-secondary)' } } }}
                    />
                    <InputTextarea
                        ref={textareaRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        autoResize
                        placeholder="Type a message"
                        className="flex-1"
                    />
                    <Button
                        icon="pi pi-paperclip"
                        text rounded
                        aria-label="Attach"
                        onClick={onAttachClick}
                        pt={{ root: { style: { width: '2.25rem', height: '2.25rem', flexShrink: 0, color: 'var(--text-color-secondary)' } } }}
                    />
                </div>
                <Button
                    icon="pi pi-send"
                    rounded
                    aria-label="Send"
                    disabled={!hasContent || sending}
                    loading={sending}
                    onClick={onSend}
                    pt={{ root: { style: { width: '2.5rem', height: '2.5rem', flexShrink: 0 } } }}
                />
            </div>

            <OverlayPanel ref={emojiRef} pt={{ content: { className: 'p-2' } }}>
                <div className="flex flex-wrap gap-1" style={{ maxWidth: '15rem' }}>
                    {QUICK_EMOJIS.map((emo) => (
                        <button
                            key={emo}
                            type="button"
                            onClick={(e) => insertEmoji(emo, e)}
                            className="bg-transparent border-none cursor-pointer hover:surface-100 transition-colors transition-duration-150 border-round"
                            style={{ fontSize: '1.35rem', padding: '0.25rem 0.4rem', lineHeight: 1 }}
                            aria-label={`Insert ${emo}`}
                        >
                            {emo}
                        </button>
                    ))}
                </div>
            </OverlayPanel>
        </div>
    );
};

// ─── Empty states ───────────────────────────────────────────────────────
const EmptyChatState = ({ onNew }) => (
    <div className="flex-1 flex align-items-center justify-content-center p-4 surface-50">
        <div className="text-center" style={{ maxWidth: '22rem' }}>
            <div
                className="flex align-items-center justify-content-center border-circle mx-auto mb-3"
                style={{ width: '4rem', height: '4rem', backgroundColor: 'var(--surface-100)' }}
            >
                <i className="pi pi-send text-500" style={{ fontSize: '1.5rem' }} aria-hidden="true" />
            </div>
            <h3 className="text-900 font-semibold m-0 mb-2">Your conversations live here</h3>
            <p className="text-600 text-sm line-height-3 m-0 mb-3">
                Pick a conversation from the list, or start a new one with someone in your network.
            </p>
            <Button label="Start a new chat" icon="pi pi-pencil" size="small" onClick={onNew} />
        </div>
    </div>
);

const EmptyConvListState = ({ searchActive, onNew }) => (
    <div className="flex flex-column align-items-center text-center gap-2 p-4 mt-3">
        <i
            className={`pi ${searchActive ? 'pi-search' : 'pi-comments'} text-400`}
            style={{ fontSize: '1.5rem' }}
            aria-hidden="true"
        />
        <p className="text-600 text-sm m-0">
            {searchActive ? 'No matches found.' : 'No conversations yet.'}
        </p>
        {!searchActive && (
            <Button label="Start a chat" icon="pi pi-pencil" size="small" outlined onClick={onNew} />
        )}
    </div>
);

// ═════════════════════════════════════════════════════════════════════════
// NEW CONVERSATION DIALOG
// ═════════════════════════════════════════════════════════════════════════
const NewConversationDialog = ({ visible, onHide, onSelect }) => {
    const [query, setQuery] = useState('');
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible) {
            setQuery('');
            return;
        }
        setLoading(true);
        ConnectionService.getConnections({ status: 'accepted', per_page: 100 })
            .then((res) => {
                if (res.success) {
                    const data = res.data?.data || res.data || [];
                    setConnections(Array.isArray(data) ? data : []);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [visible]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return connections;
        return connections.filter((c) => {
            const p = c.other_party || {};
            return (
                (p.full_name || '').toLowerCase().includes(q) ||
                (p.headline || '').toLowerCase().includes(q) ||
                (p.location_city || '').toLowerCase().includes(q)
            );
        });
    }, [connections, query]);

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            draggable={false}
            resizable={false}
            header={(
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-pencil text-primary" aria-hidden="true" />
                    <span className="text-900 font-semibold">New message</span>
                </div>
            )}
            style={{ width: 'min(560px, 95vw)' }}
            breakpoints={{ '768px': '95vw' }}
            pt={{ content: { className: 'p-0' } }}
        >
            <div className="p-3 surface-border border-bottom-1">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search connections by name, role or city…"
                        className="w-full p-inputtext-sm"
                        autoFocus
                    />
                </IconField>
            </div>
            <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                {loading ? (
                    <div className="p-3 flex flex-column gap-2">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="flex gap-2 align-items-center">
                                <Skeleton shape="circle" size="2.5rem" />
                                <div className="flex-1">
                                    <Skeleton width="60%" height="0.85rem" className="mb-2" />
                                    <Skeleton width="80%" height="0.7rem" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-5 text-600 text-sm">
                        {query ? 'No connections match your search.' : 'You don\'t have any connections yet.'}
                    </div>
                ) : (
                    <ul className="list-none p-0 m-0">
                        {filtered.map((conn, idx) => {
                            const p = conn.other_party || {};
                            const color = colorFor(p.profile_id);
                            return (
                                <li key={conn.id || idx}>
                                    <button
                                        type="button"
                                        onClick={() => onSelect(conn)}
                                        className="bg-transparent border-none cursor-pointer w-full text-left flex align-items-start gap-3 p-3 transition-colors transition-duration-150 hover:surface-50"
                                    >
                                        <Avatar
                                            label={initialsFrom(p.full_name)}
                                            image={photoUrlFor(p)}
                                            shape="circle"
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                objectFit: 'cover',
                                                backgroundColor: `var(--${color}-100)`,
                                                color: `var(--${color}-700)`,
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                border: '2px solid var(--surface-0)',
                                                boxShadow: `0 0 0 2px var(--${color}-200)`,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                {p.full_name || 'Unknown'}
                                            </div>
                                            {p.headline && (
                                                <div className="text-600 text-xs line-height-3 mt-1" style={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 1,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}>
                                                    {p.headline}
                                                </div>
                                            )}
                                            {p.location_city && (
                                                <div className="text-500 text-xs mt-1 flex align-items-center gap-1">
                                                    <i className="pi pi-map-marker" style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                                                    {p.location_city}
                                                </div>
                                            )}
                                        </div>
                                        <i className="pi pi-arrow-right text-400 align-self-center" aria-hidden="true" />
                                    </button>
                                    {idx < filtered.length - 1 && (
                                        <div className="surface-border border-top-1" style={{ marginLeft: '4rem' }} />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </Dialog>
    );
};

// ─── Misc helpers ───────────────────────────────────────────────────────
const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default Messaging;
